import styled from "styled-components";
import LandingButton from "../../../LandingButton/LandingButton";
import LandingButtonWhite from "../../../LandingButton/LandingButtonWhite";

interface CtaConfig {
  ctaTxt?: string;
  action?: string;
  pax?: string;
  toast?: string;
}

export interface BottomSheetConfig {
  heading?: string;
  icon?: string;
  title?: string;
  subtitle?: string;
  description?: string[];
  cta?: {
    primary?: CtaConfig;
    secondary?: CtaConfig;
  };
}

interface ExcelResultBottomSheetProps {
  config: BottomSheetConfig;
  onAction: (action: string, pax?: string, toast?: string) => void;
  onClose: () => void;
}

function ErrorCloudIcon() {
  return (
    <svg width="64" height="56" viewBox="0 0 64 56" fill="none" aria-hidden>
      <path
        d="M52 24a16 16 0 00-30.4-7A12 12 0 008 28a12 12 0 0012 12h32a12 12 0 000-24z"
        fill="#bdbdbd"
      />
      <circle cx="24" cy="22" r="10" fill="#e53935" />
      <path d="M24 17v6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      <circle cx="24" cy="27" r="1.2" fill="#fff" />
    </svg>
  );
}

export const ExcelResultBottomSheet = ({
  config,
  onAction,
  onClose,
}: ExcelResultBottomSheetProps) => {
  const { heading, icon, title, subtitle, description, cta } = config;
  const isErrorLayout = !heading;

  const handlePrimary = () => {
    if (cta?.primary) {
      onAction(
        cta.primary.action || "close",
        cta.primary.pax,
        cta.primary.toast
      );
    }
  };

  const handleSecondary = () => {
    if (cta?.secondary) {
      onAction(
        cta.secondary.action || "close",
        cta.secondary.pax,
        cta.secondary.toast
      );
    }
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <Sheet>
        {isErrorLayout ? (
          <>
            <ErrorCloseRow>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </CloseBtn>
            </ErrorCloseRow>
            <ErrorBody>
              <ErrorCloudIcon />
              {title ? <ErrorTitle>{title}</ErrorTitle> : null}
              {subtitle ? <ErrorSubtitle>{subtitle}</ErrorSubtitle> : null}
            </ErrorBody>
          </>
        ) : (
          <>
            <SheetHeader>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </CloseBtn>
              <SheetHeading>{heading}</SheetHeading>
            </SheetHeader>
            <SheetBody>
              {title ? <BodyTitle>{title}</BodyTitle> : null}
              {description?.length ? (
                <InfoBox>
                  {description.map((item, i) => (
                    <InfoBullet key={i}>
                      <BulletDot />
                      <span>{item}</span>
                    </InfoBullet>
                  ))}
                </InfoBox>
              ) : null}
              {subtitle ? <BodySubtitle>{subtitle}</BodySubtitle> : null}
            </SheetBody>
          </>
        )}

        <SheetFooter>
          {cta?.primary?.ctaTxt ? (
            <LandingButton
              customTitle={cta.primary.ctaTxt}
              customWidth="100%"
              customHeight="48px"
              onClick={handlePrimary}
            />
          ) : null}
          {cta?.secondary?.ctaTxt ? (
            <LandingButtonWhite
              customTitle={cta.secondary.ctaTxt}
              customWidth="100%"
              customHeight="48px"
              onClick={handleSecondary}
            />
          ) : null}
        </SheetFooter>
      </Sheet>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
`;

const Sheet = styled.div`
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 20px 20px 0 0;
  z-index: 201;
  padding-bottom: env(safe-area-inset-bottom);
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 16px 16px;
  border-bottom: 1px solid #d8d8d8;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
`;

const SheetHeading = styled.div`
  flex: 1;
  font-size: 16px;
  font-weight: 700;
  color: #000;
`;

const SheetBody = styled.div`
  padding: 20px 16px;
`;

const BodyTitle = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 400;
  color: #4a4a4a;
  line-height: 20px;
`;

const InfoBox = styled.div`
  background: #fff8e1;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoBullet = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #c77600;
  line-height: 18px;
`;

const BulletDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c77600;
  flex-shrink: 0;
`;

const BodySubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: #4a4a4a;
  line-height: 20px;
`;

const SheetFooter = styled.div`
  padding: 0 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ErrorCloseRow = styled.div`
  padding: 16px 16px 0;
`;

const ErrorBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 32px 32px;
  text-align: center;
`;

const ErrorTitle = styled.h3`
  margin: 20px 0 12px;
  font-size: 18px;
  font-weight: 900;
  color: #000;
`;

const ErrorSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: #4a4a4a;
  line-height: 20px;
`;
